import { useCallback, useEffect, useState } from 'react';
import {
  App,
  Button,
  Form,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  Upload,
} from 'antd';
import { PlusOutlined, ReloadOutlined, SearchOutlined, UploadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { api, formatBytes, formatDuration, BgmItem } from '../../services/api';
import { directCreate } from '../../services/directUpload';
import { ResourceURL } from '../../components/ResourceURL';
import { ResourceID } from '../../components/ResourceID';
import { ChecksumCell } from '../../components/ChecksumCell';
import { TagsCell } from '../../components/TagsCell';
import { AudioPreview } from '../../components/MediaPreview';
import '../media.css';

const TAGS_PLACEHOLDER = '逗号分隔，如：轻松,欢快';

export default function BgmPage() {
  const { message } = App.useApp();
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<BgmItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(12);
  const [keyword, setKeyword] = useState('');
  const [status, setStatus] = useState('all');
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<BgmItem | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [fileList, setFileList] = useState<any[]>([]);
  const [form] = Form.useForm();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.bgm.list({
        page,
        page_size: pageSize,
        keyword: keyword || undefined,
        status,
      });
      setItems(res.items || []);
      setTotal(res.total || 0);
    } catch (e) {
      message.error(e instanceof Error ? e.message : '加载失败');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, keyword, status, message]);

  useEffect(() => {
    load();
  }, [load]);

  const submit = async () => {
    const values = await form.validateFields();
    setSubmitting(true);
    try {
      if (editing) {
        if (fileList[0]?.originFileObj) {
          const fd = new FormData();
          Object.entries(values).forEach(([k, v]) => {
            if (v !== undefined && v !== null) fd.append(k, String(v));
          });
          fd.append('file', fileList[0].originFileObj);
          await api.bgm.update(editing.id, fd);
        } else {
          await api.bgm.update(editing.id, values);
        }
        message.success('已更新');
      } else {
        if (!fileList[0]?.originFileObj) {
          message.warning('请上传音乐文件');
          setSubmitting(false);
          return;
        }
        await directCreate('bgm', fileList[0].originFileObj as File, values);
        message.success('已添加');
      }
      setOpen(false);
      load();
    } catch (e) {
      message.error(e instanceof Error ? e.message : '保存失败');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <div className="media-head">
        <div>
          <Typography.Title level={3} className="tech-title" style={{ margin: 0 }}>
            音乐管理
          </Typography.Title>
          <Typography.Text type="secondary">背景音乐资源，支持试听与标签管理</Typography.Text>
        </div>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={load}>
            刷新
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setEditing(null);
              form.resetFields();
              form.setFieldsValue({ status: 'active' });
              setFileList([]);
              setOpen(true);
            }}
          >
            添加音乐
          </Button>
        </Space>
      </div>

      <div className="tech-panel media-toolbar">
        <Space wrap>
          <Input
            allowClear
            prefix={<SearchOutlined />}
            placeholder="搜索名称 / 描述 / 标签"
            style={{ width: 220 }}
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            onPressEnter={() => {
              setPage(1);
              load();
            }}
          />
          <Select
            style={{ width: 120 }}
            value={status}
            onChange={setStatus}
            options={[
              { value: 'all', label: '全部状态' },
              { value: 'active', label: '启用' },
              { value: 'disabled', label: '停用' },
            ]}
          />
          <Button
            type="primary"
            ghost
            onClick={() => {
              setPage(1);
              load();
            }}
          >
            查询
          </Button>
        </Space>
      </div>

      <div className="tech-panel media-table">
        <Table
          rowKey="id"
          loading={loading}
          dataSource={items}
          scroll={{ x: 1740 }}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            onChange: (p, ps) => {
              setPage(p);
              setPageSize(ps);
            },
          }}
          columns={[
            {
              title: 'ID',
              dataIndex: 'resource_id',
              width: 180,
              render: (v: string) => <ResourceID id={v} />,
            },
            {
              title: '预览',
              width: 220,
              render: (_: unknown, row: BgmItem) => <AudioPreview url={row.file_url} />,
            },
            {
              title: '曲目',
              dataIndex: 'name',
              render: (v: string, row: BgmItem) => (
                <div>
                  <div className="media-name">{v}</div>
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {formatDuration(row.duration_ms)} · {formatBytes(row.file_size)}
                  </Typography.Text>
                </div>
              ),
            },
            {
              title: 'URL',
              dataIndex: 'file_url',
              width: 300,
              render: (v: string) => <ResourceURL url={v} />,
            },
            {
              title: 'Checksum',
              dataIndex: 'checksum',
              width: 160,
              render: (v: string) => <ChecksumCell value={v} />,
            },
            {
              title: '标签',
              dataIndex: 'tags',
              width: 180,
              render: (v: string) => <TagsCell value={v} />,
            },
            {
              title: '状态',
              dataIndex: 'status',
              width: 90,
              render: (v: string) => (
                <Tag color={v === 'active' ? 'success' : 'default'}>{v === 'active' ? '启用' : '停用'}</Tag>
              ),
            },
            {
              title: '更新',
              dataIndex: 'updated_at',
              width: 160,
              render: (v: string) => dayjs(v).format('YYYY-MM-DD HH:mm'),
            },
            {
              title: '操作',
              width: 150,
              render: (_: unknown, row: BgmItem) => (
                <Space>
                  <Button
                    type="link"
                    onClick={() => {
                      setEditing(row);
                      form.setFieldsValue(row);
                      setFileList([]);
                      setOpen(true);
                    }}
                  >
                    编辑
                  </Button>
                  <Popconfirm
                    title="确认删除？"
                    onConfirm={async () => {
                      await api.bgm.remove(row.id);
                      message.success('已删除');
                      load();
                    }}
                  >
                    <Button type="link" danger>
                      删除
                    </Button>
                  </Popconfirm>
                </Space>
              ),
            },
          ]}
        />
      </div>

      <Modal
        title={editing ? '编辑音乐' : '添加音乐'}
        open={open}
        onCancel={() => setOpen(false)}
        onOk={submit}
        confirmLoading={submitting}
        width={640}
        destroyOnClose
      >
        <Form form={form} layout="vertical" style={{ marginTop: 12 }}>
          <Form.Item name="name" label="曲名" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="description" label="描述">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item name="tags" label="标签">
            <Input placeholder={TAGS_PLACEHOLDER} />
          </Form.Item>
          <Form.Item name="status" label="状态" rules={[{ required: true }]}>
            <Select
              options={[
                { value: 'active', label: '启用' },
                { value: 'disabled', label: '停用' },
              ]}
            />
          </Form.Item>
          <Form.Item label={editing ? '替换文件（可选）' : '上传文件'} required={!editing}>
            <Upload
              beforeUpload={() => false}
              maxCount={1}
              accept="audio/*"
              fileList={fileList}
              onChange={({ fileList: fl }) => setFileList(fl)}
            >
              <Button icon={<UploadOutlined />}>选择音频</Button>
            </Upload>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
